import { RiCalendarLine, RiErrorWarningLine, RiTimeLine } from '@remixicon/react';
import { cva, type VariantProps } from 'class-variance-authority';
import React, { useRef } from 'react';
import { type DateValue, Popover } from 'react-aria-components';
import { cn } from '../utils';
import CalendarPopover from './CalendarPopover';
import DateTextInput from './DateTextInput';
import TimePickerPopover from './TimePickerPopover';
import TimeTextInput from './TimeTextInput';
import { useDatePickerController } from './useDatePickerController';

// ─── Styling ─────────────────────────────────────────────────────────────────

const wrapperVariants = cva(
  'inline-flex items-center w-full bg-white border transition-all duration-150 cursor-text overflow-hidden',
  {
    variants: {
      variant: { default: 'border-neutral-5 hover:border-neutral-6', error: 'border-red-text' },
      size: { sm: 'h-[32px] px-2.5 text-14', md: 'h-[40px] px-3 text-14', lg: 'h-[48px] px-4 text-16' },
      shape: { rounded: 'rounded-md', pill: 'rounded-full' },
      isFocused: { true: '', false: '' },
      isDisabled: { true: 'opacity-60 pointer-events-none bg-neutral-2', false: '' },
    },
    compoundVariants: [
      { variant: 'default', isFocused: true, className: 'border-brand-6 ring-2 ring-ring-primary' },
      { variant: 'error', isFocused: true, className: 'ring-2 ring-ring-destructive' },
    ],
    defaultVariants: { variant: 'default', size: 'md', shape: 'rounded', isFocused: false, isDisabled: false },
  }
);

const labelVariants = cva('text-neutral-text font-medium', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-14' } },
  defaultVariants: { size: 'md' },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type WrapperVariantProps = VariantProps<typeof wrapperVariants>;

export interface AgDatePickerProps {
  label?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  helperText?: string;
  size?: WrapperVariantProps['size'];
  shape?: WrapperVariantProps['shape'];
  className?: string;
  placeholder?: string;
  granularity?: 'day' | 'hour' | 'minute' | 'second';
  hourCycle?: 12 | 24;
  calendarFooter?: React.ReactNode;
  value?: DateValue | null;
  onChange?: (value: DateValue | null) => void;
  onFocusChange?: (isFocused: boolean) => void;
  minValue?: DateValue;
  maxValue?: DateValue;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function AgDatePicker({
  label,
  isRequired,
  isDisabled,
  isInvalid,
  errorMessage: errorMessageProp,
  helperText,
  size = 'md',
  shape = 'rounded',
  className,
  placeholder = 'M/D/YYYY',
  granularity = 'day',
  hourCycle = 12,
  calendarFooter,
  value,
  onChange,
  onFocusChange,
  minValue,
  maxValue,
}: AgDatePickerProps) {
  const inputId = React.useId();
  const triggerRef = useRef<HTMLDivElement>(null);
  const c = useDatePickerController({
    value,
    onChange,
    onFocusChange,
    granularity,
    hourCycle,
    minValue,
    maxValue,
    isDisabled,
  });

  // `granularity` is narrowed here because child inputs only receive it when `c.hasTime` is true,
  // which guarantees granularity !== 'day'. The cast avoids duplicating the guard.
  const timeGranularity = granularity as 'hour' | 'minute' | 'second';

  const statusMessage = (() => {
    const msg = errorMessageProp ?? c.internalError;
    if (msg)
      return (
        <span className="absolute left-0 top-full pt-1 flex items-center gap-1 text-12 text-red-text whitespace-nowrap">
          <RiErrorWarningLine className="h-3.5 w-3.5 shrink-0" />
          {msg}
        </span>
      );
    if (helperText)
      return (
        <span className="absolute left-0 top-full pt-1 text-12 text-neutral-alpha-7 whitespace-nowrap">
          {helperText}
        </span>
      );
    return null;
  })();

  return (
    <div
      ref={c.containerRef}
      className={cn(`flex ${c.hasTime ? 'min-w-60' : 'min-w-30'} flex-col gap-2 w-full`, className)}
      onFocus={c.handleContainerFocus}
      onBlur={c.handleContainerBlur}
      onKeyDown={c.onContainerKeyDown}
    >
      {label && (
        <label htmlFor={inputId} className={cn(labelVariants({ size }))}>
          {label}
          {isRequired && <span className="text-red-text ml-2">*</span>}
        </label>
      )}

      <div className="relative" ref={triggerRef}>
        <div
          onClick={c.onWrapperClick}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) c.dateInputRef.current?.focus();
          }}
          className={cn(
            wrapperVariants({
              variant: isInvalid || !!c.internalError ? 'error' : 'default',
              size,
              shape,
              isFocused: c.isFocused,
              isDisabled,
            })
          )}
        >
          <DateTextInput
            inputId={inputId}
            inputRef={c.dateInputRef}
            value={c.dateText}
            onChange={c.onDateTextChange}
            onCommit={c.onDateCommit}
            onAdvanceToTime={c.onAdvanceToTime}
            onKeyDown={(e) => {
              if (e.key === 'Enter') c.onEnterInDate();
              c.onArrowKeyInDate(e);
            }}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            isRequired={isRequired}
          />
          {c.hasTime && (
            <TimeTextInput
              inputRef={c.timeInputRef}
              value={c.timeText}
              onChange={c.onTimeTextChange}
              onCommit={c.onTimeCommit}
              onFocus={() => {
                // Setting mode + open directly here keeps this presentational-only side effect
                // out of the controller while preserving the auto-open-on-focus behavior.
                // The controller's handleContainerFocus also handles this for mouse focus.
              }}
              onOpenPicker={c.onOpenTimePicker}
              onRetreatToDate={c.onRetreatToDate}
              onEnterSegmentMode={c.onEnterSegmentMode}
              granularity={timeGranularity}
              hourCycle={hourCycle}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              isRequired={isRequired}
            />
          )}
          <div className="ml-auto flex items-center gap-0.5 -mr-2 shrink-0">
            <button
              type="button"
              tabIndex={-1}
              className="outline-none p-1 rounded-md hover:bg-neutral-2 transition-colors cursor-pointer"
              onClick={c.onCalendarIconClick}
              disabled={isDisabled}
            >
              <RiCalendarLine className="h-4 w-4 text-neutral-alpha-7" />
            </button>
            {c.hasTime && (
              <button
                type="button"
                tabIndex={-1}
                className="outline-none p-1 rounded-md hover:bg-neutral-2 transition-colors cursor-pointer"
                onClick={c.onClockIconClick}
                disabled={isDisabled}
              >
                <RiTimeLine className="h-4 w-4 text-neutral-alpha-7" />
              </button>
            )}
          </div>
        </div>

        <Popover
          triggerRef={triggerRef}
          isOpen={c.popoverOpen}
          onOpenChange={(open) => {
            if (!open) c.closePopover();
          }}
          isNonModal
          placement="bottom end"
          offset={8}
          style={{ zIndex: 210001 }}
          className="outline-none"
        >
          {c.popoverMode === 'calendar' ? (
            <CalendarPopover
              ref={c.popoverRef}
              value={c.calendarValue}
              onSelect={c.handleCalendarSelect}
              minValue={minValue}
              maxValue={maxValue}
              calendarFooter={calendarFooter}
            />
          ) : c.hasTime ? (
            <TimePickerPopover
              ref={c.popoverRef}
              hour={c.timePickerValue.hour}
              minute={c.timePickerValue.minute}
              second={c.timePickerValue.second}
              granularity={timeGranularity}
              hourCycle={hourCycle}
              onChange={c.handleTimePickerChange}
              minTime={c.timeConstraints.minTime}
              maxTime={c.timeConstraints.maxTime}
            />
          ) : null}
        </Popover>
        {statusMessage}
      </div>
    </div>
  );
}

AgDatePicker.displayName = 'AgDatePicker';
